import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import {
  getApiV1IndividualpaymentFinancialPeopleFinancialpersonidHeaderQueryKey,
  getApiV1IndividualpaymentFinancialPeopleFinancialpersonidParcelsQueryKey,
  usePatchApiV1PecegepayCreditcardCoursefinpersonpaymentidRefund,
} from '@/shared/api/generated';

import type { PlanWriteError } from '../utils/planWriteError';
import { classifyPlanWriteError } from '../utils/planWriteError';

/**
 * Teto de caracteres do motivo da devolução.
 *
 * Como o corpo da NF do pagamento futuro, `refundReason` é **query param**: o
 * texto viaja na URL, e servidor e proxy têm limite para a linha de request
 * inteira. O limite no campo transforma um erro opaco em algo que o operador vê
 * antes de enviar.
 */
export const REFUND_REASON_MAX_LENGTH = 500;

/** O que o operador informa no diálogo de devolução. */
export interface ParcelRefundInput {
  /** Valor a devolver, em reais. `null` manda a request sem o parâmetro. */
  readonly refundValue: number | null;
  /** Motivo da devolução. Vazio é omitido da request. */
  readonly refundReason: string;
}

/**
 * Devolução do pagamento de uma parcela paga em cartão.
 *
 * Cobre `PATCH /pecegepay/creditcard/{courseFinPersonPaymentId}/refund` — o
 * `courseFinPersonPaymentId` **é** o id da parcela. Como o endpoint é o do
 * provedor, chamá-lo já executa o estorno na Mundipagg/Pagar.me: não existe o
 * caminho "só registrar aqui" que o legado oferecia por checkbox.
 *
 * **Dois campos do diálogo legado não têm parâmetro** e por isso não são pedidos:
 * o checkbox de estorno no provedor (implícito, acima) e a lista de e-mails de
 * aviso. O cabeçalho do contrato tem `refundSendMail` e `refundCreditCard`, que
 * parecem ser exatamente esses dois comportamentos, mas são campos de **leitura**
 * — nenhum endpoint de escrita os aceita. Enquanto isso, o aviso por e-mail não
 * sai desta tela.
 *
 * Os dois parâmetros são opcionais: valor ausente pede a devolução integral, que
 * é o padrão do endpoint, e motivo em branco é omitido em vez de gravar string
 * vazia.
 *
 * Invalida as parcelas e o cabeçalho do contrato — a devolução move `refundValue`
 * e `refundDate` na linha e os totais do contrato. Os boletos ficam de fora: a
 * devolução não emite nem cancela boleto.
 *
 * @param financialPersonId - Contrato da rota; `null` só pula a invalidação.
 * @param parcelId - Parcela alvo; `null` torna o `refund` inerte, evitando
 *   request com `NaN` na URL.
 * @returns `{ refund, isPending, isError, error, reset }`
 *
 * @example
 * ```tsx
 * const { refund, isPending } = useParcelRefund(101, 42);
 * refund({ refundValue: 220, refundReason: 'Desistência' }, { onSuccess: close });
 * ```
 */
export function useParcelRefund(financialPersonId: number | null, parcelId: number | null) {
  const queryClient = useQueryClient();
  const mutation = usePatchApiV1PecegepayCreditcardCoursefinpersonpaymentidRefund();

  const refund = useCallback(
    (
      input: ParcelRefundInput,
      options?: {
        readonly onSuccess?: () => void;
        readonly onError?: (error: PlanWriteError) => void;
      },
    ) => {
      if (parcelId === null) return;

      const refundReason = input.refundReason.trim();

      // `exactOptionalPropertyTypes` está ligado: chave presente com `undefined`
      // não equivale a chave ausente, então os campos vazios são omitidos.
      const params = {
        ...(input.refundValue === null ? {} : { refundValue: input.refundValue }),
        ...(refundReason === '' ? {} : { refundReason }),
      };

      mutation.mutate(
        {
          courseFinPersonPaymentId: parcelId,
          ...(Object.keys(params).length === 0 ? {} : { params }),
        },
        {
          onSuccess: () => {
            if (financialPersonId !== null) {
              void queryClient.invalidateQueries({
                queryKey:
                  getApiV1IndividualpaymentFinancialPeopleFinancialpersonidParcelsQueryKey(
                    financialPersonId,
                  ),
              });
              void queryClient.invalidateQueries({
                queryKey:
                  getApiV1IndividualpaymentFinancialPeopleFinancialpersonidHeaderQueryKey(
                    financialPersonId,
                  ),
              });
            }

            options?.onSuccess?.();
          },
          onError: error => options?.onError?.(classifyPlanWriteError(error)),
        },
      );
    },
    [financialPersonId, parcelId, mutation, queryClient],
  );

  return {
    refund,
    isPending: mutation.isPending,
    isError: mutation.isError,
    /** Falha classificada, com a mensagem do backend quando houver. */
    error: classifyPlanWriteError(mutation.error),
    reset: mutation.reset,
  } as const;
}
